import {
  DEFAULT_SHELTER_PALETTE_ID,
  SHELTER_PALETTE_CATALOG,
  normalizeAdoptionQuizThemeMode,
  type AdoptionQuizThemeMode,
  type ShelterPaletteId,
  resolvePrivateThemeVars,
} from './shelterPaletteCatalog';

/**
 * Browser-only cache for the **signed-in admin** private app theme (`PrivateAppThemeRoot`).
 * Settings → Customization **public quiz** theme is persisted via `PATCH /shelters/:id`
 * (`settings.adoptionQuizTheme`), not this store.
 */

function isValidPaletteId(id: unknown): id is ShelterPaletteId {
  if (id === DEFAULT_SHELTER_PALETTE_ID) return true;
  return (
    typeof id === 'string' &&
    SHELTER_PALETTE_CATALOG.some((p) => p.id === id)
  );
}

export type PersistedShelterBranding = {
  paletteId: ShelterPaletteId;
  mode: AdoptionQuizThemeMode;
};

const STORAGE_KEY = 'ffl.shelterPrivateTheme.v1';

type StorageShape = {
  version: 1;
  /** Keyed by `shelterId` or `"_global"` when no tenant id. */
  byShelter: Record<string, PersistedShelterBranding>;
};

function emptyStore(): StorageShape {
  return { version: 1, byShelter: {} };
}

function readStore(): StorageShape {
  if (typeof window === 'undefined') return emptyStore();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw) as StorageShape;
    if (parsed?.version !== 1 || typeof parsed.byShelter !== 'object') {
      return emptyStore();
    }
    return parsed;
  } catch {
    return emptyStore();
  }
}

function writeStore(store: StorageShape) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

export function shelterBrandingStorageKey(shelterId: string | undefined): string {
  return shelterId?.trim() ? shelterId.trim() : '_global';
}

export function readPersistedShelterBranding(
  shelterKey: string,
): PersistedShelterBranding {
  const store = readStore();
  const found = store.byShelter[shelterKey];
  if (!found) {
    return { paletteId: DEFAULT_SHELTER_PALETTE_ID, mode: 'light' };
  }
  const paletteId = isValidPaletteId(found.paletteId)
    ? found.paletteId
    : DEFAULT_SHELTER_PALETTE_ID;
  const mode = normalizeAdoptionQuizThemeMode(found.mode) ?? 'light';
  return { paletteId, mode };
}

export function writePersistedShelterBranding(
  shelterKey: string,
  value: PersistedShelterBranding,
) {
  if (typeof window === 'undefined') return;
  const store = readStore();
  store.byShelter[shelterKey] = value;
  writeStore(store);
  window.dispatchEvent(new CustomEvent('ffl-private-shelter-theme-changed'));
}

/** Variables we may set from JS — must clear all when reverting to stylesheet defaults. */
const MANAGED_THEME_KEYS = [
  '--theme-background',
  '--theme-foreground',
  '--theme-muted',
  '--theme-muted-foreground',
  '--theme-border',
  '--theme-card',
  '--theme-card-foreground',
  '--theme-primary-foreground',
  '--theme-accent',
  '--theme-accent-foreground',
  '--theme-destructive',
  '--theme-destructive-foreground',
  '--theme-header',
  '--theme-header-foreground',
  '--theme-header-muted',
  '--theme-highlight',
  '--theme-highlight-foreground',
  '--theme-success',
  '--theme-success-foreground',
  '--theme-popover',
  '--theme-popover-foreground',
  '--theme-primary-50',
  '--theme-primary-100',
  '--theme-primary-200',
  '--theme-primary-300',
  '--theme-primary-400',
  '--theme-primary-500',
  '--theme-primary-600',
  '--theme-primary-700',
  '--theme-primary-800',
  '--theme-primary-900',
  '--theme-primary-950',
] as const;

export function clearPrivateThemeInlineOverrides(el: HTMLElement) {
  for (const key of MANAGED_THEME_KEYS) {
    el.style.removeProperty(key);
  }
}

export function applyPrivateShelterTheme(
  el: HTMLElement,
  branding: PersistedShelterBranding,
) {
  const resolveMode: 'light' | 'dark' =
    branding.mode === 'light' ? 'light' : 'dark';
  const vars = resolvePrivateThemeVars(branding.paletteId, resolveMode);
  if (!vars) {
    clearPrivateThemeInlineOverrides(el);
    return;
  }
  for (const [k, v] of Object.entries(vars) as [string, string][]) {
    el.style.setProperty(k, v);
  }
}
